<template>
  <v-navigation-drawer rail permanent>
    <div class="d-flex flex-column gr-2 align-center py-3 h-100">
      <AppNavigationItem
        v-for="item in items"
        :key="item.feature"
        :icon="item.icon"
        :title="item.title"
        :to="item.to"
      />
      <v-spacer />
      <AppNavigationItem
        v-if="permissions.is_admin"
        icon="fas fa-screwdriver-wrench"
        :title="t('admin.title')"
        :to="{ name: '/admin/' }"
      />
    </div>
  </v-navigation-drawer>
</template>

<script lang="ts" setup>
import { useCurrentSpace } from '@/composables/use-spaces'
import { useUserPermissions } from '@/composables/use-user-permissions'
import AppNavigationItem from './AppNavigationItem.vue'
import type { SpaceFeature } from '@/constants/spaces'
import type { RouteLocationRaw } from 'vue-router'

type NavigationItem = {
  icon: string
  title: string
  to: RouteLocationRaw
  visible: boolean
}

const { t } = useI18n()

const { permissions } = useUserPermissions()
const { currentSpaceConfig, currentSpaceParam } = useCurrentSpace()

// without a space key for general, otherwise links to /kb aren't active when the route has no space param
const spaceParams = computed(() =>
  currentSpaceParam.value ? { space: currentSpaceParam.value } : {},
)

const navigationItems = computed<Record<SpaceFeature, NavigationItem>>(() => ({
  kb: {
    icon: 'fas fa-lightbulb-on',
    title: t('knowledge-base'),
    to: { name: '/[[space]]/kb/', params: spaceParams.value },
    visible: true,
  },
  tickets: {
    icon: 'fas fa-ticket',
    title: 'Tickets',
    to: { name: '/tickets/' },
    visible: permissions.value.can_edit,
  },
  chat: {
    icon: 'fas fa-comment',
    title: t('chat.title'),
    to: { name: '/[[space]]/chat/', params: spaceParams.value },
    visible: true,
  },
  jira: {
    icon: 'fab fa-jira',
    title: 'Jira',
    to: { name: '/jira/' },
    visible: permissions.value.can_search_jira,
  },
  ops: {
    icon: 'fas fa-server',
    title: t('ops.cases'),
    to: { name: '/ops/' },
    visible: permissions.value.can_edit,
  },
}))

const items = computed(() =>
  currentSpaceConfig.value.features
    .map((feature) => ({ feature, ...navigationItems.value[feature] }))
    .filter((item) => item.visible),
)
</script>
